import React from "react"
import type { PropsWithStyle } from "@mallardbay/lib-react-components"
import {
    Button,
    ColorSchemes,
    componentStyles,
    HorizontalStack,
} from "@mallardbay/lib-react-components"

import useIsSmallerThanMediumScreen from "~utils/hooks/use-is-smaller-than-medium-screen"
import type { OutfitterAssistantAgent } from "~components/outfitter-assistant/outfitter-assistant-constants"
import COPY from "~config/copy-constants"
import { useTrackAiSuggestionEvents } from "~utils/event-tracking/event-tracking"
import type { AiAgent } from "~graphql/generated/types"

interface Props extends PropsWithStyle {
    readonly aiAgent: AiAgent | OutfitterAssistantAgent
    readonly selectedAiSuggestionId: string | null
    readonly cancelSuggestion: () => void
    readonly acceptSuggestion: (suggestionId: string) => void
    readonly isLoadingAcceptSuggestion: boolean
    readonly shouldHideOnDesktop?: boolean
}

export default function SmartScheduleConfirmationButtons({
    style,
    aiAgent,
    selectedAiSuggestionId,
    cancelSuggestion,
    acceptSuggestion,
    isLoadingAcceptSuggestion,
    shouldHideOnDesktop,
}: Props) {
    const styles = useStyles()
    const isMobile = useIsSmallerThanMediumScreen()
    const isValidScreenSize = isMobile || !shouldHideOnDesktop

    const { trackAcceptSuggestion, trackCancelSuggestion } =
        useTrackAiSuggestionEvents({ aiAgent })

    if (!selectedAiSuggestionId || !isValidScreenSize) return null

    return (
        <HorizontalStack style={style}>
            <Button
                style={styles.button}
                onClick={() => {
                    cancelSuggestion()
                    trackCancelSuggestion(selectedAiSuggestionId)
                }}
                isDisabled={isLoadingAcceptSuggestion}
            >
                {COPY.CANCEL}
            </Button>
            <Button
                style={styles.button}
                onClick={() => {
                    void acceptSuggestion(selectedAiSuggestionId)
                    trackAcceptSuggestion(selectedAiSuggestionId)
                }}
                colorScheme={ColorSchemes.Brand}
                isLoading={isLoadingAcceptSuggestion}
            >
                {COPY.ACCEPT}
            </Button>
        </HorizontalStack>
    )
}

function useStyles() {
    return componentStyles({
        button: {
            width: "full",
        },
    })
}
